// 1. Framework / library imports
import React, { useCallback, useMemo, useState } from "react";

// 2. Helpers & Hooks
import { DropdownCacheKey, buildDropdownCacheKey, useDropdownCache } from "services/hooks/useDropdownCache";

// 3. API & Types
import { getVehicleTypesAPI } from "services/api/catalog/vehicles/types/api";
import { VehicleTypeRow } from "services/api/catalog/vehicles/types/types";
import { DataWithPagination } from "services/api";
import { initialVehicleTypeDataWithPagination } from "./consts";

// 4. UI Components
import { DropdownAutocomplete } from "../DropdownAutocomplete";
import { DropdownOption } from "../Dropdown/types";
import VehicleTypeTooltip from "./components/VehicleTypeTooltip";

// 5. Local Types
import { VehicleTypeDropdownProps } from "./types";

const FIVE_MINUTES = 5 * 60 * 1000;

const VehicleTypeDropdown: React.FC<VehicleTypeDropdownProps> = (
  props: VehicleTypeDropdownProps
) => {
  const {
    vehicleManufacturerId,
    vehicleModelId,
    name,
    disabled,
    required,
    label,
    error,
    value,
    direction,
    width,
    height,
    placeholder,
    onChange,
    autoOpen
  } = props;

  const isDisabled = useMemo(() => {
    if (!vehicleManufacturerId) return true;
    if (!vehicleModelId) return true;
    if (disabled) return true;
    return false;
  }, [disabled, vehicleManufacturerId, vehicleModelId]);

  const [searchValue, setSearchValue] = useState<string>("");

  const { data, isLoading } = useDropdownCache<DataWithPagination<VehicleTypeRow>>(
    buildDropdownCacheKey(DropdownCacheKey.VehicleTypes, vehicleManufacturerId, vehicleModelId, searchValue),
    () => getVehicleTypesAPI(vehicleManufacturerId!, vehicleModelId!, initialVehicleTypeDataWithPagination.pagination, searchValue),
    FIVE_MINUTES,
    !!vehicleManufacturerId && !!vehicleModelId
  );

  const searchResults = (vehicleManufacturerId && vehicleModelId && data) ? data : initialVehicleTypeDataWithPagination;

  const handleSearchClick = useCallback(() => {
    setSearchValue("");
  }, []);

  const handleSearchChange = useCallback((name: string) => {
    setSearchValue(name);
  }, []);

  const formatYear = (yearFrom: number | null, yearTo: number | null): string => {
    if (yearFrom && yearTo) return `${yearFrom} - ${yearTo}`;
    if (yearFrom) return `${yearFrom} -`;
    return "";
  };

  const vehicleModelItems: DropdownOption[] = searchResults.data.map((i) => {
    const power = i.maximumPowerKw && i.maximumPowerHp
      ? `${i.maximumPowerKw} kW / ${i.maximumPowerHp} KS`
      : i.maximumPowerKw
        ? `${i.maximumPowerKw} kW`
        : i.maximumPowerHp
          ? `${i.maximumPowerHp} KS`
          : null;
    const details = [power, i.fuelType?.name ?? null, i.transmission?.name ?? null]
      .filter(Boolean)
      .join(" / ");
    const year = formatYear(i.yearFrom, i.yearTo);
    const displayName = [i.name, details].filter(Boolean).join(" · ");
    return {
      value: i.id,
      text: (
        <div className="flex justify-between items-center w-full gap-2">
          <span className="flex items-center gap-2">
            <span>{i.name}</span>
            {details && <span className="text-slate-400 text-xs">{details}</span>}
          </span>
          {year && <span className="text-slate-400 text-xs whitespace-nowrap">{year}</span>}
        </div>
      ),
      displayName,
      image: i.image,
      // Detalji na hover slike u popisu — redak ih ne stigne pokazati.
      tooltip: <VehicleTypeTooltip type={i} />,
    };
  });

  // Odabrani tip — isti tooltip stoji i nakon odabira, uz vrijednost u polju.
  const selectedType = useMemo(
    () => searchResults.data.find((type) => type.id === value),
    [searchResults.data, value]
  );

  return (
    <>
      <DropdownAutocomplete
        label={label}
        name={name}
        disabled={isDisabled}
        required={required}
        placeholder={placeholder}
        value={value}
        direction={direction}
        width={width}
        height={height}
        options={vehicleModelItems}
        onChange={onChange}
        onSearchChange={handleSearchChange}
        onSearchFocus={handleSearchClick}
        error={error}
        autoOpen={autoOpen}
        isLoading={isLoading}
        tooltip={selectedType ? <VehicleTypeTooltip type={selectedType} /> : undefined}
      />
    </>
  );
};

export default React.memo(VehicleTypeDropdown);
